import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'wouter';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { ArrowLeft, FileText, Book, Lightbulb, Info, ChevronLeft, ChevronRight, Play, Maximize, Minimize, X } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

// Simple KeyTakeaway component
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <Lightbulb className="text-blue-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-blue-700 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Slide interface
interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
  image?: string;
}

// Slideshow component
const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const slideshowRef = useRef<HTMLDivElement>(null);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };
  
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      // Enter fullscreen
      if (slideshowRef.current?.requestFullscreen) {
        slideshowRef.current.requestFullscreen().catch(err => {
          console.error(`Error attempting to enable fullscreen: ${err.message}`);
        });
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(err => {
          console.error(`Error attempting to exit fullscreen: ${err.message}`);
        });
      }
    }
  };
  
  // Add event listeners for fullscreen change
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (slides.length === 0) return null;

  return (
    <div 
      ref={slideshowRef}
      className={`relative ${isFullscreen ? 'fixed inset-0 z-50 flex items-center justify-center bg-white' : 'bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl overflow-hidden mb-10'}`}
    >
      <div className={`${isFullscreen ? 'px-10 py-20 w-full h-full flex items-center justify-center' : 'px-6 py-12 md:px-12 md:py-16'}`}>
        <div className={`${isFullscreen ? 'max-w-6xl w-full' : 'max-w-4xl mx-auto'}`}>
          <div className="space-y-6">
            <h2 className={`${isFullscreen ? 'text-3xl md:text-4xl text-blue-900' : 'text-2xl md:text-3xl text-blue-900'} font-serif text-center font-bold`}>
              {slides[currentSlide].title}
            </h2>
            <div className={`prose prose-lg max-w-none ${isFullscreen ? 'prose-strong:text-blue-700' : 'prose-strong:text-blue-700'} overflow-y-auto ${isFullscreen ? 'max-h-[calc(100vh-180px)]' : 'max-h-[60vh]'}`} style={{scrollbarWidth: 'thin'}}>
              {slides[currentSlide].content}
            </div>
            {slides[currentSlide].image && (
              <div className="mt-6 flex justify-center">
                <div className={`${isFullscreen ? 'bg-gray-50 p-3' : 'bg-white p-2'} rounded-lg shadow-lg`}>
                  <img 
                    src={slides[currentSlide].image} 
                    alt={slides[currentSlide].title}
                    className={`${isFullscreen ? 'max-h-[500px]' : 'max-h-[300px]'} object-contain rounded`}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {isFullscreen && (
        <button
          onClick={toggleFullscreen}
          className="absolute top-4 right-4 text-gray-500 bg-gray-100 p-2 rounded-full hover:bg-gray-200"
        >
          <X className="h-6 w-6" />
        </button>
      )}

      <div className="absolute bottom-4 right-4 flex space-x-2">
        <Button
          size="sm"
          variant="outline"
          className={`${isFullscreen ? 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200' : 'bg-white/80 hover:bg-white'}`}
          onClick={prevSlide}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          className={`${isFullscreen ? 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200' : 'bg-white/80 hover:bg-white'}`}
          onClick={nextSlide}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          className={`${isFullscreen ? 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200' : 'bg-white/80 hover:bg-white'}`}
          onClick={toggleFullscreen}
        >
          {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
        </Button>
      </div>

      <div className="absolute bottom-4 left-4">
        <span className={`text-sm font-medium ${isFullscreen ? 'bg-gray-100 text-gray-700' : 'bg-white/80'} px-2 py-1 rounded`}>
          {currentSlide + 1} / {slides.length}
        </span>
      </div>
    </div>
  );
};

// Video component
const VideoPlaceholder = () => {
  return (
    <div className="bg-gray-100 rounded-xl overflow-hidden my-10">
      <div className="aspect-video bg-gray-200 flex items-center justify-center relative">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-indigo-500/20"></div>
        <div className="text-center">
          <div className="bg-blue-600 text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 shadow-lg hover:bg-blue-700 cursor-pointer transition-colors">
            <Play className="h-8 w-8" />
          </div>
          <p className="text-gray-700 font-medium">Watch Full Lecture Video</p>
          <p className="text-sm text-gray-500 mt-1">45 minutes · Premium Content</p>
        </div>
      </div>
    </div>
  );
};

export default function QuestioningGoldStandard() {
  const [_, navigate] = useLocation();
  
  // Lecture slides data
  const lectureSlides: Slide[] = [
    {
      id: 1,
      title: "The Tension Between Data and Reality",
      content: (
        <>
          <p>Few concepts loom larger in modern medicine than the randomized controlled trial (RCT). Whether we're evaluating a new drug, an exercise intervention, or a dietary strategy, the refrain of "RCTs are the gold standard" echoes through the halls of hospitals, universities, and regulatory bodies.</p>
          
          <p>But beneath this veneration lies a brewing tension: in our quest to find population-wide truths, we may be losing sight of the individual nuances that make each person unique. As data-driven technologies push medicine toward hyper-personalization, some are asking whether RCTs — and the statistical tools embedded in them — still suffice to guide individualized clinical decisions.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
            <div className="bg-white p-3 rounded-lg shadow-md">
              <h3 className="text-lg font-medium text-blue-800 mb-2">Traditional Approach</h3>
              <ul className="space-y-2 list-disc list-inside text-gray-700">
                <li>Population-based statistics</li>
                <li>P-values and binary outcomes</li>
                <li>"Average" patient model</li>
                <li>Reliance on large sample sizes</li>
                <li>Rigid inclusion/exclusion criteria</li>
              </ul>
            </div>
            
            <div className="bg-white p-3 rounded-lg shadow-md">
              <h3 className="text-lg font-medium text-blue-800 mb-2">Emerging Challenges</h3>
              <ul className="space-y-2 list-disc list-inside text-gray-700">
                <li>Personalized medicine needs</li>
                <li>Biological uniqueness</li>
                <li>Complex interventions</li>
                <li>Subtle effect variations</li>
                <li>Real-world translation gaps</li>
              </ul>
            </div>
          </div>
          
          <p className="mt-4">This lesson explores that tension. It looks under the hood of RCTs and associated statistical constructs to expose significant limitations that can warp our understanding of "what works." It asks whether we've become so reliant on outcomes like p-values that we neglect deeper insights about mechanism, physiology, and context.</p>
        </>
      )
    },
    {
      id: 2,
      title: "The Rise and Reign of RCTs",
      content: (
        <>
          <p>To appreciate the limitations of RCTs, it helps to begin with how they became so central in medicine. The randomized controlled trial first gained momentum in the mid-20th century. The British Medical Research Council's landmark trial on streptomycin for tuberculosis in 1948 is often cited as one of the earliest well-designed RCTs.</p>
          
          <div className="bg-blue-50 p-4 rounded-lg my-4">
            <h3 className="text-lg font-medium text-blue-900 mb-2">Basic Logic of an RCT</h3>
            <ol className="list-decimal list-inside space-y-2 text-gray-800">
              <li><strong>Randomization:</strong> Participants are randomly assigned to the intervention group or the control (placebo or standard care) group. This random assignment, in theory, balances both known and unknown confounders across groups.</li>
              <li><strong>Blinding:</strong> Ideally, neither the participant nor the researcher knows who's receiving the treatment or the placebo. Double-blind designs reduce the risk of bias from patients' or clinicians' expectations.</li>
              <li><strong>Comparison:</strong> Outcomes from the intervention group are compared with those from the control group. If there's a statistically significant difference, we might conclude the intervention has an effect beyond placebo or usual care.</li>
            </ol>
          </div>
          
          <p>By the 1990s, <strong>Evidence-Based Medicine (EBM)</strong> emerged as a formal movement. EBM placed RCTs at the top of a hierarchy of evidence—systematic reviews and meta-analyses of RCTs being the ultimate gold.</p>
          
          <div className="bg-white p-4 rounded-lg shadow-md my-4">
            <h3 className="text-lg font-medium text-red-800 mb-2">Practical Limitations of RCTs</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex">
                <span className="mr-2">•</span>
                <span><strong>Complex interventions</strong> like nutrition, exercise, or mental health strategies often struggle to meet the rigid "blindable" RCT design.</span>
              </li>
              <li className="flex">
                <span className="mr-2">•</span>
                <span><strong>Rare conditions</strong> lack the large sample sizes an RCT demands.</span>
              </li>
              <li className="flex">
                <span className="mr-2">•</span>
                <span><strong>Heterogeneous conditions</strong> like metabolic syndrome or autoimmune disorders may respond inconsistently to the same intervention.</span>
              </li>
              <li className="flex">
                <span className="mr-2">•</span>
                <span><strong>Cost and feasibility</strong> can make large RCTs unworkable for many promising ideas.</span>
              </li>
            </ul>
          </div>
        </>
      )
    },
    {
      id: 3,
      title: "The Problem with P-Values and Binary Outcomes",
      content: (
        <>
          <p>When we say a study found a "statistically significant" result, we typically mean the p-value crossed a threshold (often p &lt; 0.05). That threshold, 0.05, is an arbitrary convention that gained traction through historical precedent rather than an ironclad law of nature.</p>
          
          <div className="bg-white p-4 rounded-lg shadow-md my-4">
            <h3 className="text-lg font-medium text-blue-900 mb-3">The Limitations of P-Values</h3>
            
            <div className="space-y-4">
              <div className="flex">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-red-800 font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Sample Size Sensitivity</h4>
                  <p className="text-gray-700 text-sm">Large studies can turn trivial differences into "significance" - a difference too small to matter clinically may still be statistically significant with a large enough sample.</p>
                </div>
              </div>
              
              <div className="flex">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-red-800 font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">No Magnitude Information</h4>
                  <p className="text-gray-700 text-sm">A p-value provides no direct estimate of the magnitude of effect - knowing something works "significantly" tells us nothing about how much it helps.</p>
                </div>
              </div>
              
              <div className="flex">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-red-800 font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Vulnerable to Misuse</h4>
                  <p className="text-gray-700 text-sm">P-hacking, selective reporting, and publication bias can dramatically distort what appears in the literature. Negative results often go unpublished.</p>
                </div>
              </div>
              
              <div className="flex">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-red-800 font-bold">4</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Black-and-White Thinking</h4>
                  <p className="text-gray-700 text-sm">Encourages a binary mindset that obscures important variations and nuances in biological responses.</p>
                </div>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            While p-values can help shield us from pure subjectivity, they are not the final word on whether an intervention is clinically or personally worthwhile. More refined approaches—confidence intervals, Bayesian analysis, effect sizes—help fill in some gaps.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 4,
      title: "The External Validity Trap",
      content: (
        <>
          <p>One of the core premises of RCT design is internal validity—the confidence that differences in outcomes between the intervention and control arms can be attributed to the intervention rather than biases or confounders. But external validity, or generalizability, often gets overshadowed.</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-medium text-blue-800 mb-2 border-b pb-2">RCT Population</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-baseline">
                  <span className="mr-2 text-blue-500">•</span>
                  <span>Younger, healthier subjects</span>
                </li>
                <li className="flex items-baseline">
                  <span className="mr-2 text-blue-500">•</span>
                  <span>Often less diverse ethnically</span>
                </li>
                <li className="flex items-baseline">
                  <span className="mr-2 text-blue-500">•</span>
                  <span>Few comorbidities</span>
                </li>
                <li className="flex items-baseline">
                  <span className="mr-2 text-blue-500">•</span>
                  <span>Fixed dosing protocols</span>
                </li>
                <li className="flex items-baseline">
                  <span className="mr-2 text-blue-500">•</span>
                  <span>Limited outcome measures</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-medium text-blue-800 mb-2 border-b pb-2">Real-World Population</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-baseline">
                  <span className="mr-2 text-blue-500">•</span>
                  <span>Full age spectrum</span>
                </li>
                <li className="flex items-baseline">
                  <span className="mr-2 text-blue-500">•</span>
                  <span>Diverse genetic backgrounds</span>
                </li>
                <li className="flex items-baseline">
                  <span className="mr-2 text-blue-500">•</span>
                  <span>Multiple comorbidities</span>
                </li>
                <li className="flex items-baseline">
                  <span className="mr-2 text-blue-500">•</span>
                  <span>Varied medication regimens</span>
                </li>
                <li className="flex items-baseline">
                  <span className="mr-2 text-blue-500">•</span>
                  <span>Personalized dosing needs</span>
                </li>
              </ul>
            </div>
          </div>
          
          <p>Mechanistic complexity adds another layer of difficulty. Human biology is not a linear machine; it's a dynamic network with feedback loops. Genetics, microbiomes, and epigenetic factors can create distinct responses to the same intervention.</p>
          
          <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-500 my-4">
            <p className="text-amber-900">While RCTs can robustly test an intervention's efficacy in a controlled setting, the leap to effectiveness in the messy real world is not guaranteed. When clinicians or policymakers apply these "average" data to everyone, they risk ignoring how unique physiology, environment, and preferences can shape an individual's response.</p>
          </div>
        </>
      )
    },
    {
      id: 5,
      title: "Mechanisms and Models – How Other Sciences Do It Differently",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-medium text-indigo-900 mb-2">Physics</h3>
              <p className="text-gray-700 text-sm">Theoretical models predict phenomena that can be experimentally confirmed. Einstein's general relativity predicted gravitational lensing, later confirmed by observation.</p>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-medium text-indigo-900 mb-2">Engineering</h3>
              <p className="text-gray-700 text-sm">Relies on first-principle analyses to design structures. You don't randomly build half the bridges with steel and half with wood, waiting to see which collapse.</p>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-medium text-indigo-900 mb-2">Evolutionary Biology</h3>
              <p className="text-gray-700 text-sm">A robust theoretical model (Darwinian natural selection) guides experimentation across genetics, ecology, and paleontology.</p>
            </div>
          </div>
          
          <p>Medicine, by contrast, has historically been more of an empirical discipline. Before the 20th century, treatments were often based on anecdote or tradition. The rise of RCTs represented a leap forward, demanding that therapies be systematically tested.</p>
          
          <p>Yet in doing so, medicine placed heavy emphasis on frequentist statistics: we test the null hypothesis (e.g., "Drug X has no effect") and see if we can reject it with a p-value &lt; 0.05. The overarching mechanism—how Drug X exerts its effect—often becomes secondary to statistical significance.</p>
          
          <div className="bg-white p-4 rounded-lg shadow-md my-4">
            <h3 className="text-lg font-medium text-blue-900 mb-3">Consequences of Under-Prioritizing Mechanisms</h3>
            
            <ul className="space-y-3">
              <li className="flex">
                <span className="text-red-500 mr-2">•</span>
                <span className="text-gray-800">Treatments proven statistically but poorly understood biologically</span>
              </li>
              <li className="flex">
                <span className="text-red-500 mr-2">•</span>
                <span className="text-gray-800">Difficult to predict who will respond to a treatment</span>
              </li>
              <li className="flex">
                <span className="text-red-500 mr-2">•</span>
                <span className="text-gray-800">Increased risk of side effects from undetermined pathways</span>
              </li>
              <li className="flex">
                <span className="text-red-500 mr-2">•</span>
                <span className="text-gray-800">Less ability to refine interventions when they don't work</span>
              </li>
              <li className="flex">
                <span className="text-red-500 mr-2">•</span>
                <span className="text-gray-800">Barriers to innovation when mechanisms aren't valued</span>
              </li>
            </ul>
          </div>
        </>
      )
    },
    {
      id: 6,
      title: "The Personalization Puzzle – Why 'Average Effects' Can Mislead",
      content: (
        <>
          <p>Even if we accept that RCTs yield valuable data, there's still the question: How relevant is the average effect for me or my patient? All of us have encountered scenarios in which a drug that "works" for most patients does nothing for a particular individual, or worse, triggers adverse reactions.</p>
          
          <div className="bg-indigo-50 p-4 rounded-lg my-6">
            <h3 className="text-lg font-medium text-indigo-900 mb-3">N-of-One Trials: Individual-Level Evidence</h3>
            
            <p className="mb-3 text-indigo-900">An n-of-one trial is essentially a single-person experiment where that individual cycles through different interventions or dose levels in a structured manner, tracking objective and subjective outcomes.</p>
            
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <h4 className="font-medium text-indigo-800 mb-2">Example: Chronic Migraine Patient</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-gray-200 rounded p-3">
                  <p className="font-medium text-gray-900 mb-1">RCT Data:</p>
                  <p className="text-gray-700 text-sm">New migraine medication reduces monthly headache days by two for the <em>average</em> participant.</p>
                </div>
                
                <div className="border border-green-200 rounded p-3 bg-green-50">
                  <p className="font-medium text-gray-900 mb-1">N-of-One Results:</p>
                  <p className="text-gray-700 text-sm">The same medication reduces this <em>specific patient's</em> headaches by 10 days a month.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">Challenges with N-of-One Approach</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-baseline">
                  <span className="mr-2 text-red-500">•</span>
                  <span>Ensuring blinding (minimizing placebo effects)</span>
                </li>
                <li className="flex items-baseline">
                  <span className="mr-2 text-red-500">•</span>
                  <span>Collecting high-quality individual data</span>
                </li>
                <li className="flex items-baseline">
                  <span className="mr-2 text-red-500">•</span>
                  <span>Managing spontaneously fluctuating conditions</span>
                </li>
                <li className="flex items-baseline">
                  <span className="mr-2 text-red-500">•</span>
                  <span>Resource intensity of personalized tracking</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">Best Applications</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-baseline">
                  <span className="mr-2 text-green-500">•</span>
                  <span>Chronic, stable conditions</span>
                </li>
                <li className="flex items-baseline">
                  <span className="mr-2 text-green-500">•</span>
                  <span>Precisely measurable variables (blood glucose, blood pressure)</span>
                </li>
                <li className="flex items-baseline">
                  <span className="mr-2 text-green-500">•</span>
                  <span>Interventions with rapid observable effects</span>
                </li>
                <li className="flex items-baseline">
                  <span className="mr-2 text-green-500">•</span>
                  <span>Motivated patients willing to track outcomes</span>
                </li>
              </ul>
            </div>
          </div>
          
          <KeyTakeaway>
            The n-of-one approach shifts medicine's focus from "Does it work on average?" to "Does it work for this individual, and by how much?" This complements RCT data, bridging the gap between group-level statistics and personal outcomes.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 7,
      title: "The Hidden Costs of an Overreliance on Biostatistics",
      content: (
        <>
          <p>While it's clear that RCTs and standard biostatistics have propelled medicine forward, there is a hidden cost to overrelying on these structures:</p>
          
          <div className="space-y-6 my-6">
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-medium text-red-800 mb-2 flex items-center">
                <span className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mr-2">
                  <span className="text-red-800">1</span>
                </span>
                Inhibition of Innovation
              </h3>
              <p className="text-gray-700 ml-10">Because RCTs can be expensive, time-consuming, and complex, only well-funded entities (often large pharmaceutical companies) can afford them on a large scale. This filters out many smaller interventions—nutritional protocols, certain lifestyle adjustments, or old generic drugs repurposed—because nobody is incentivized to conduct a huge trial.</p>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-medium text-red-800 mb-2 flex items-center">
                <span className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mr-2">
                  <span className="text-red-800">2</span>
                </span>
                Intellectual Stagnation
              </h3>
              <p className="text-gray-700 ml-10">When guidelines strictly require RCT data, we risk undervaluing mechanistic research or observational signals. A potentially beneficial therapy might have a strong mechanistic rationale, plus observational data suggesting effectiveness, but still be dismissed until someone conducts an expensive trial.</p>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-medium text-red-800 mb-2 flex items-center">
                <span className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mr-2">
                  <span className="text-red-800">3</span>
                </span>
                Publication Bias and Industry Influence
              </h3>
              <p className="text-gray-700 ml-10">Because journals tend to favor "positive" or novel results, negative or inconclusive RCTs often remain unpublished. This skews the literature, a phenomenon sometimes called the file drawer effect. And because industry funds a large share of trials, investigators might design studies that favor commercially viable outcomes.</p>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-medium text-red-800 mb-2 flex items-center">
                <span className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mr-2">
                  <span className="text-red-800">4</span>
                </span>
                Overemphasis on a Single Metric
              </h3>
              <p className="text-gray-700 ml-10">The reliance on a single primary outcome measure—like mortality, weight change, or cholesterol levels—can obscure other important outcomes such as quality of life, functional capacity, or mental health. Additionally, a therapy might have off-target effects that aren't captured by the designated primary endpoint.</p>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-lg font-medium text-red-800 mb-2 flex items-center">
                <span className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center mr-2">
                  <span className="text-red-800">5</span>
                </span>
                Reduction of Patient Autonomy
              </h3>
              <p className="text-gray-700 ml-10">If guidelines label an intervention "ineffective" due to lack of RCT evidence—yet a particular patient's physiology or preference suggests it might help—clinicians can be conflicted about prescribing it. Strict guidelines sometimes hamper the art of medicine, the nuanced back-and-forth between physician and patient that leads to personalized strategies.</p>
            </div>
          </div>
          
          <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
            <p className="text-blue-900">It bears repeating that none of this means RCTs are useless. Rather, it suggests that an excessive or exclusive focus on RCTs and p-values can stifle a broader understanding. A more pluralistic approach would incorporate multiple levels of evidence—mechanistic plausibility, animal studies, observational data, small pilot trials, pragmatic trials, real-world evidence, n-of-one experiments, and yes, large RCTs.</p>
          </div>
        </>
      )
    },
    {
      id: 8,
      title: "Bridging the Gap – Bayesian Thinking, Mechanistic Insights, and Pragmatic Trials",
      content: (
        <>
          <p>If p-values and RCTs have shortcomings, what are the alternatives or complements? Three major avenues stand out:</p>
          
          <div className="space-y-6 my-6">
            <div className="bg-white rounded-lg shadow p-5">
              <div className="flex">
                <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-indigo-800 font-bold text-xl">B</span>
                </div>
                <div>
                  <h3 className="text-xl font-medium text-indigo-900 mb-2">Bayesian Analysis</h3>
                  <p className="text-gray-700">Unlike frequentist methods that rely on a null hypothesis, Bayesian approaches incorporate prior probabilities and update the probability of a hypothesis as new data emerge. This allows for a more continuous interpretation of evidence, rather than a pass/fail at p=0.05.</p>
                  <p className="text-gray-700 mt-2">If we have strong mechanistic or observational reasons to believe a therapy might help, Bayesian analysis can weight that prior knowledge and show how new data modifies our confidence.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-5">
              <div className="flex">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-green-800 font-bold text-xl">M</span>
                </div>
                <div>
                  <h3 className="text-xl font-medium text-green-900 mb-2">Mechanistic Research</h3>
                  <p className="text-gray-700">Investing in deeper biological insights can refine how we conduct trials. Imagine we know from cell culture and animal models that a certain compound modulates a key aging pathway (mTOR, for instance).</p>
                  <p className="text-gray-700 mt-2">Rather than running an enormous trial with a broad endpoint like "all-cause mortality," we might design a smaller trial measuring biomarkers that reflect mTOR activity, exploring how different dosages affect subgroups with varying genetics.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-5">
              <div className="flex">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-amber-800 font-bold text-xl">P</span>
                </div>
                <div>
                  <h3 className="text-xl font-medium text-amber-900 mb-2">Pragmatic and Adaptive Trials</h3>
                  <p className="text-gray-700">Traditional RCTs often take place under strict protocols that don't resemble real-world conditions. Pragmatic trials try to preserve randomization while loosening protocols so they reflect everyday practice.</p>
                  <p className="text-gray-700 mt-2">Adaptive trials can modify certain parameters (like sample size or eligibility criteria) mid-study based on interim results, optimizing for what's learned along the way.</p>
                </div>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            Bayesian thinking, mechanistic exploration, and innovative trial designs collectively move us closer to a model where we're not locked into a binary "significant vs. not significant" mindset. Rather, we can weigh evidence iteratively, refine hypotheses, and adapt to new data.
          </KeyTakeaway>
        </>
      )
    },
    {
      id: 9,
      title: "Practical Steps for Personalized (n-of-One) Investigation",
      content: (
        <>
          <p>Though lofty in concept, n-of-one approaches can be integrated into real clinical practice or self-experimentation. Here's a practical roadmap:</p>
          
          <div className="bg-white rounded-lg shadow-lg p-6 my-6">
            <h3 className="text-xl font-medium text-blue-900 mb-4 border-b pb-2">N-of-One Investigation Protocol</h3>
            
            <div className="space-y-6">
              <div className="flex">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-blue-800 font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Identify a Measurable Endpoint</h4>
                  <p className="text-gray-700">Choose an outcome that's meaningful to the individual—blood glucose variability, migraine frequency, sleep quality, or lab biomarkers like LDL-P or CRP. Clarity on what to measure is crucial.</p>
                </div>
              </div>
              
              <div className="flex">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-blue-800 font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Establish Baseline</h4>
                  <p className="text-gray-700">Track that outcome without intervention (or on the current standard care) for a set period. Collect data consistently, ideally with validated tools (e.g., continuous glucose monitors, migraine diaries).</p>
                </div>
              </div>
              
              <div className="flex">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-blue-800 font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Introduce the Intervention</h4>
                  <p className="text-gray-700">This could be a new supplement, a dietary change, a different exercise routine, or a medication. Maintain the same tracking protocol. If feasible, blinding can reduce placebo effects.</p>
                </div>
              </div>
              
              <div className="flex">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-blue-800 font-bold">4</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Switch or Washout</h4>
                  <p className="text-gray-700">If you're testing multiple interventions or comparing a new therapy to baseline, incorporate a washout period where you revert to baseline conditions to clear any residual effects. Then try the second intervention.</p>
                </div>
              </div>
              
              <div className="flex">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-blue-800 font-bold">5</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Crossover</h4>
                  <p className="text-gray-700">In a classical n-of-one approach, you cross over from intervention to control (or a second intervention), then switch back again, collecting data in each phase.</p>
                </div>
              </div>
              
              <div className="flex">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-blue-800 font-bold">6</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Analyze Outcomes</h4>
                  <p className="text-gray-700">Plot the data to see if the variable changed significantly during the intervention vs. baseline. A statistical test could be used—like a paired t-test across repeated measurements—but visual inspection and descriptive statistics may suffice for personal decisions.</p>
                </div>
              </div>
              
              <div className="flex">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-blue-800 font-bold">7</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Refine</h4>
                  <p className="text-gray-700">If the intervention helps, keep it. If it doesn't or yields side effects, discard it. If partially helpful, adjust dosage or timing, repeating the cycle to optimize results.</p>
                </div>
              </div>
            </div>
          </div>
          
          <p>While not foolproof, this iterative process honors the uniqueness of individual biology more faithfully than relying on average RCT results alone. For conditions resistant to standard treatments or for those seeking to optimize performance (like elite athletes or individuals pursuing longevity), the n-of-one path can be transformative.</p>
        </>
      )
    },
    {
      id: 10,
      title: "Conclusion – A Vision Beyond the 'Gold Standard'",
      content: (
        <>
          <p>RCTs have earned their reputation by shielding us from biases that historically riddled medicine. Yet, as this lesson demonstrates, they're not without significant limitations:</p>
          
          <ul className="list-disc list-inside space-y-2 my-4 ml-4">
            <li>They often prioritize average results over individual variations.</li>
            <li>They rely on p-values that can be misleading or trivial.</li>
            <li>They can be prohibitively expensive, skewing research toward commercially viable interventions.</li>
            <li>They may fail to capture complex or mechanistic insights that drive true breakthroughs.</li>
          </ul>
          
          <p>Meanwhile, other scientific fields show us the power of combining theoretical models with empirical tests, building a deep understanding of cause and effect rather than black-box statistical associations.</p>
          
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 my-6">
            <h3 className="text-xl font-serif font-medium text-blue-900 mb-4">A Balanced Paradigm for the Future</h3>
            
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">1</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Mechanistic Foundation</h4>
                  <p className="text-gray-700">Using preclinical research, animal models, or physiologic data to understand the how and why of an intervention.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">2</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Adaptive Trial Structures</h4>
                  <p className="text-gray-700">Employing RCTs that reflect real-world conditions, using pragmatic or adaptive designs to gather valid data without sacrificing flexibility.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">3</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Bayesian and Other Statistical Approaches</h4>
                  <p className="text-gray-700">Interpreting results in a continuum—updating prior beliefs, considering effect sizes, and acknowledging that significance is not black and white.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mr-3 flex-shrink-0 mt-1">
                  <span className="text-blue-800 font-bold">4</span>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Personalized Confirmation</h4>
                  <p className="text-gray-700">Where feasible, validating an intervention's impact at the individual level, whether through rigorous n-of-one trials or close clinical follow-up that monitors relevant biomarkers.</p>
                </div>
              </div>
            </div>
          </div>
          
          <KeyTakeaway>
            The future of medicine, especially in longevity and performance optimization, will hinge on bridging the gap between population-level evidence and personal variability. By questioning whether our current gold standard truly meets every need—and by integrating innovative trial designs, mechanistic research, and n-of-one experimentation—we can chart a path that yields more nuanced, effective, and patient-centered care.
          </KeyTakeaway>
          
          <div className="mt-6 text-sm text-gray-600 border-t pt-4">
            <p className="font-medium">References and Suggested Readings</p>
            <ul className="space-y-1 mt-2">
              <li>Ioannidis JPA. "Why Most Published Research Findings Are False." PLOS Medicine, 2005.</li>
              <li>Freedman B. "Equipoise and the Ethics of Randomized Clinical Research." New England Journal of Medicine, 1987.</li>
              <li>Guyatt GH et al. "Evidence-Based Medicine: A New Approach to Teaching the Practice of Medicine." JAMA, 1992.</li>
              <li>Kravitz RL, Duan N, Braslow J. "Evidence-Based Medicine, Heterogeneity of Treatment Effects, and the Trouble with Averages." Millbank Quarterly, 2004.</li>
              <li>Berry DA. Bayesian Methods in Health Technology Assessment. In "Statistical Science in the Courtroom," 2000.</li>
              <li>N-of-1 Trials in Healthcare. Agency for Healthcare Research and Quality, 2014.</li>
            </ul>
          </div>
        </>
      )
    }
  ];

  // Check for authentication

  return (
    <MentorshipAccessGate>
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 flex-grow max-w-5xl">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship">Mentorship</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship/lectures">Lectures</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink>Questioning the Gold Standard</BreadcrumbLink>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            className="flex items-center text-[#3498DB]"
            onClick={() => navigate("/mentorship")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Mentorship
          </Button>
          
          <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
            Lecture Material
          </div>
        </div>
        
        <div>
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
            Questioning the Gold Standard
          </h1>
          <p className="text-[#7F8C8D] mb-6">
            A critical examination of the limitations of randomized controlled trials and conventional 
            biostatistics in the era of personalized medicine, with practical alternatives.
          </p>
          <div className="flex items-center gap-3 mb-8">
            <div className="flex items-center text-sm text-[#7F8C8D]">
              <FileText className="h-4 w-4 mr-1" />
              <span>Lecture Duration: 45 minutes</span>
            </div>
            <div className="w-1 h-1 rounded-full bg-gray-300"></div>
            <div className="flex items-center text-sm text-[#7F8C8D]">
              <Book className="h-4 w-4 mr-1" />
              <span>Last Updated: April 12, 2025</span>
            </div>
          </div>
          
          <Separator />
          
          <Slideshow slides={lectureSlides} />
          
          {/* Note about live lecture */}
          <div className="mt-8 text-center">
            <p className="text-gray-600 italic">There was no live lecture for this lesson.</p>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
    </MentorshipAccessGate>
  );
}